import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Attach Access Token from localStorage before each request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Fetch the current business owner's dashboard data:
 * - business profile info
 * - stats (totalReviews, avgRating, responseRate, totalViews, ratingDistribution)
 * - recentReviews (last 10)
 */
export const fetchBusinessDashboard = async () => {
  const response = await apiClient.get('/business/me');
  return response.data.data;
};

/**
 * Fetch all reviews for the current business with optional filters and pagination.
 * 
 * Params:
 * - status: 'all' | 'approved' | 'pending' | 'flagged'
 * - rating: filter by star rating (1-5) or 'all'
 * - search: search in review title/content
 * - sort_by: 'created_at' | 'rating' | 'helpful_count'
 * - order: 'asc' | 'desc'
 * - limit: number of reviews per page (default 10, max 50)
 * - offset: pagination offset (default 0)
 * 
 * Example: fetchBusinessReviews({ status: 'approved', rating: 5, limit: 20, offset: 0 })
 */
export const fetchBusinessReviews = async (filters = {}) => {
  const {
    status = 'all',
    rating = 'all',
    search = '',
    sort_by = 'created_at',
    order = 'desc',
    limit = 10,
    offset = 0,
  } = filters;

  const queryParams = new URLSearchParams({
    status,
    rating,
    ...(search && { search }),
    sort_by,
    order,
    limit,
    offset,
  }).toString();

  const response = await apiClient.get(`/business/reviews?${queryParams}`);
  return response.data.data;
};

/**
 * Fetch count of pending reviews for current business
 */
export const fetchPendingReviewsCount = async () => {
  const response = await apiClient.get('/business/reviews/pending/count');
  return response.data.data;
};

/**
 * Fetch review velocity counts by time window
 * Returns: { thisWeek, lastWeek, thisMonth, lastMonth }
 */
export const fetchReviewVelocity = async () => {
  const response = await apiClient.get('/business/reviews/velocity');
  return response.data.data;
};

/**
 * Fetch quick stats snapshot for the business owner.
 * Lighter endpoint than /me for quick dashboard updates.
 */
export const fetchBusinessStats = async () => {
  const response = await apiClient.get('/business/stats');
  return response.data.data;
};

/**
 * Respond to a review (future feature - not yet implemented in backend)
 */
export const respondToReview = async (reviewId, responseText) => {
  const response = await apiClient.post(`/business/reviews/${reviewId}/respond`, {
    response: responseText,
  });
  return response.data;
};

/**
 * Flag/report a review as inappropriate (future feature - not yet implemented in backend)
 */
export const flagReview = async (reviewId, reason) => {
  const response = await apiClient.post(`/business/reviews/${reviewId}/flag`, {
    reason,
  });
  return response.data;
};

/**
 * Fetch complete business profile information
 */
export const fetchBusinessProfile = async () => {
  const response = await apiClient.get('/business/profile');
  return response.data.data;
};

/**
 * Update business basic information (name, category, description, website)
 */
export const updateBusinessBasicInfo = async (data) => {
  const response = await apiClient.put('/business/profile/basic', data);
  return response.data.data;
};

/**
 * Update business contact information (email, phone, address)
 */
export const updateBusinessContact = async (data) => {
  const response = await apiClient.put('/business/profile/contact', data);
  return response.data.data;
};

/**
 * Update business opening hours
 * data format: { monday: {open: "09:00", close: "18:00", closed: false}, ... }
 */
export const updateBusinessHours = async (hours) => {
  const response = await apiClient.put('/business/profile/hours', { opening_hours: hours });
  return response.data.data;
};

/**
 * Change business account password
 */
export const changeBusinessPassword = async (data) => {
  const response = await apiClient.post('/business/profile/change-password', data);
  return response.data.data;
};

/**
 * Update business profile information (legacy - use specific update functions)
 */
export const updateBusinessProfile = async (businessData) => {
  const response = await apiClient.put('/business/profile', businessData);
  return response.data;
};

export default apiClient;
